import { HttpClient } from '@angular/common/http';
import { TranslateLoader } from '@ngx-translate/core';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AppUtils } from '../utils/app.utils';
import { LocalStorageUtils } from '../utils/localStorage.utils';
import { AssetTypes } from './enums/assetTypes';

export class TranslateHttpLoader implements TranslateLoader {
  env: string = environment.name;

  constructor(
    private httpClient: HttpClient,
  ) {
  }

  /**
   * Retrieves translation, feature toggle, and configuration data for the given language.
   * - Clears unused cached translation data from local storage.
   * - Checks if an update is required for the language data.
   *   - If an update is required:
   *     - Calls the Arakh API to fetch translation, feature toggle, and configuration data.
   *     - Sanitizes the fetched data by removing unnecessary properties.
   *     - Combines the sanitized data into a single object and returns it.
   *   - If an update is not required:
   *     - Retrieves cached data for translations, feature toggles, and configurations from local storage.
   *     - Combines the cached data into a single object and returns it.
   *
   * @param {string} lang - The language identifier.
   * @returns {Observable<object>} - An observable containing the combined data.
   */
  getTranslation(lang: string): Observable<object> {
    this._clearUnusedCachedTranslationData();
    const storeId: string = LocalStorageUtils.getKeyValue('storeId');

    /**
     * Checks if the map page is loaded with a wrong language.
     * This is a workaround for the map page that is not loaded with the default language but JP or DE.
     *
     * @type {boolean}
     */
    const isMapPageWithWrongLang = window.location.pathname === '/' && !lang.includes('en');

    if (this._isUpdateRequired(lang) || isMapPageWithWrongLang) {
      return this._updateArakhDataForNewLang(isMapPageWithWrongLang ? 'en' : lang, storeId);
    }
    return this._getArakhDataFromLocalStorage(lang);
  }

  /**
   * Recursively normalizes the given data so every numeric/boolean value is a string.
   *
   * @param {unknown} data
   * @returns {unknown}
   */
  normalizeValuesToStrings(data: unknown): unknown {
    if (Array.isArray(data)) {
      return data.map((item: unknown) => this.normalizeValuesToStrings(item));
    }
    if (data !== null && typeof data === 'object') {
      return Object.fromEntries(
        Object.entries(data).map(([key, value]: [string, unknown]) => [key, this.normalizeValuesToStrings(value)]),
      );
    }
    if (typeof data === 'number' || typeof data === 'boolean') {
      return data.toString();
    }
    return data;
  }

  /**
   * Fetches the latest translations, feature toggles, and configs from the API for the specified language.
   * @param {string} lang
   * @param {string} storeId
   * @returns {Observable<Object>}
   * @private
   */
  private _updateArakhDataForNewLang(lang: string, storeId: string): Observable<Object> {
    const arakhTranslations: Observable<object> = this._arakhApiCall(lang, AssetTypes.TRANSLATIONS, storeId);
    const arakhFeatureToggles: Observable<object> = this._arakhApiCall(lang, AssetTypes.FEATURE_TOGGLES, storeId);
    const arakhConfigs: Observable<object> = this._arakhApiCall(lang, AssetTypes.CONFIGS, storeId);

    return forkJoin([
      arakhTranslations,
      arakhFeatureToggles,
      arakhConfigs,
    ]).pipe(
      map(
        ([arakhTranslations, arakhFeatureToggles, arakhConfigs]) => {
          const sanitizedFeatureToggles = {...arakhFeatureToggles};
          if ('config' in sanitizedFeatureToggles) {
            delete sanitizedFeatureToggles.config;
          }

          const sanitizedConfigs = {...arakhConfigs};
          if ('feature_toggle' in sanitizedConfigs) {
            delete sanitizedConfigs.feature_toggle;
          }
          return {
            ...arakhTranslations,
            ...sanitizedFeatureToggles,
            ...sanitizedConfigs,
          };
        },
      ),
    );
  }

  /**
   * Fetches translations, feature toggles, and configs from local storage for the specified language.
   * @param {string} lang
   * @returns {Observable<Object>}
   * @private
   */
  private _getArakhDataFromLocalStorage(lang: string): Observable<Object> {
    return forkJoin([
      of(JSON.parse(LocalStorageUtils.getKeyValue(`lang-data-${lang}`))),
      of(JSON.parse(LocalStorageUtils.getKeyValue(`feature-toggles-data-${lang}`))),
      of(JSON.parse(LocalStorageUtils.getKeyValue(`config-data-${lang}`))),
    ]).pipe(
      map(([arakhTranslations, arakhFeatureToggles, arakhConfigs]) => {
          return {
            ...arakhTranslations,
            ...arakhFeatureToggles,
            ...arakhConfigs,
          };
        },
      ),
    );
  }

  /**
   * Makes an API call to fetch data based on the type of call and language.
   * - For `local` environment, retrieves local data instead of making an API call.
   * - Constructs the store identifier and API URL for the request.
   * - Handles different types of calls (`translations`, `feature-toggles`, or `configs`).
   * - Updates the language update date for `translations` type.
   * - Fetches data with a fallback mechanism in case of errors.
   *
   * @param {string} lang - The language identifier.
   * @param {AssetTypes} typeOfCall - The type of data to fetch (`translations`, `feature-toggles`, or `configs`).
   * @param {string} storeId - The store identifier.
   *
   * @returns {Observable<object>} - An observable containing the fetched data.
   * @private
   */
  private _arakhApiCall(lang: string, typeOfCall: AssetTypes, storeId: string): Observable<object> {
    if (this.env === 'local') {
      return this._getLocalAsset(lang, storeId, typeOfCall);
    }

    let assetType: string;
    let arakhUrl: string;
    const store: string = this._getStoreConfiguration(true, typeOfCall, lang, storeId);

    switch (typeOfCall) {
      case AssetTypes.TRANSLATIONS:
        arakhUrl = `${environment.arakhUrl}/i18n${store}.json`;
        assetType = 'lang-data';
        break;
      case AssetTypes.FEATURE_TOGGLES:
        arakhUrl = `${environment.arakhUrl}/toggles${store}.json`;
        assetType = 'feature-toggles-data';
        break;
      default:
        arakhUrl = `${environment.arakhUrl}/config${store}.json`;
        assetType = 'config-data';
    }
    this._setUpdateDate(lang);

    return this._fetchDataWithFallback(arakhUrl, assetType, lang, store);
  }

  /**
   * Performs an HTTP GET request to fetch data and stores it in local storage.
   * Normalizes the fetched data so all values are strings.
   * Provides a fallback mechanism to retrieve local data in case of an error.
   *
   * @param {string} arakhUrl - The API URL to fetch data from.
   * @param {string} assetType - The type of asset being fetched (e.g., `config-data`, `feature-toggles-data`, `lang-data`).
   * @param {string} lang - The language identifier (e.g., `en`, `fr`).
   * @param {string} [storeId=''] - The store identifier (optional).
   *
   * @returns {Observable<object>} - An observable containing the processed data.
   * - If the HTTP request succeeds, the data is fetched, processed, and stored in local storage.
   * - If the HTTP request fails, local data is retrieved as a fallback.
   */
  private _fetchDataWithFallback(arakhUrl: string, assetType: string, lang: string, storeId: string = ''): Observable<object> {
    return this.httpClient.get<object>(arakhUrl).pipe(
      map(data => {
        const processedData: object = this.normalizeValuesToStrings(data) as object;
        LocalStorageUtils.setKeyValue(`${assetType}-${lang}`, JSON.stringify(processedData));
        return processedData;
      }),
      catchError((): Observable<object> => {
        switch (assetType) {
          case 'feature-toggles-data':
            return this._getLocalAsset(lang, storeId, AssetTypes.FEATURE_TOGGLES);
          case 'config-data':
            return this._getLocalAsset(lang, storeId, AssetTypes.CONFIGS);
          default:
            return this._getLocalAsset(lang, storeId, AssetTypes.TRANSLATIONS);
        }
      }),
    );
  }

  /**
   * Retrieves local asset data for the specified language and asset type.
   * - Constructs the URLs for the default and localized files based on the asset type.
   * - If the default and localized file URLs are the same, fetches and stores the data directly.
   * - Otherwise, merges the default and localized assets into a single object.
   *
   * @param {string} lang - The language identifier (e.g., 'en', 'fr').
   * @param {string} storeId - The store identifier.
   * @param {AssetTypes} type - The type of asset to retrieve (e.g., translations, feature toggles, configs).
   *
   * @returns {Observable<object>} - An observable containing the fetched or merged asset data.
   * @private
   */
  private _getLocalAsset(lang: string, storeId: string, type: AssetTypes): Observable<object> {
    const store: string = this._getStoreConfiguration(false, type, lang, storeId);

    let localDefaultFileUrl: string;
    let localLocalizedFileUrl: string;
    let assetType: string;

    switch (type) {
      case AssetTypes.TRANSLATIONS:
        localDefaultFileUrl = `/assets/arakh_i18n/i18n-en.json`;
        localLocalizedFileUrl = `/assets/arakh_i18n/i18n${store}.json`;
        assetType = 'lang-data';
        break;
      case AssetTypes.FEATURE_TOGGLES:
        localDefaultFileUrl = `/assets/arakh_feature_toggles/toggles.json`;
        localLocalizedFileUrl = `/assets/arakh_feature_toggles/toggles${store}.json`;
        assetType = 'feature-toggles-data';
        break;
      case AssetTypes.CONFIGS:
        localDefaultFileUrl = `/assets/arakh_configs/config.json`;
        localLocalizedFileUrl = `/assets/arakh_configs/config${store}.json`;
        assetType = 'config-data';
        break;
    }

    if (localDefaultFileUrl === localLocalizedFileUrl) {
      return this._fetchAndStoreLocalData(localDefaultFileUrl, assetType, lang);
    }

    return this._mergeLocalAssets(lang, localDefaultFileUrl, localLocalizedFileUrl, assetType);
  }

  /**
   * Performs an HTTP GET request to fetch data from the given URL and stores it in local storage.
   * Normalizes the fetched data so all values are strings.
   *
   * @param {string} localDefaultFileUrl - The URL of the file to fetch.
   * @param {string} assetType - The type of asset being fetched (e.g., lang-data, feature-toggles-data).
   * @param {string} lang - The language identifier.
   *
   * @returns {Observable<object>} - An observable containing the fetched data.
   * @private
   */
  private _fetchAndStoreLocalData(localDefaultFileUrl: string, assetType: string, lang: string): Observable<object> {
    return this.httpClient.get<object>(localDefaultFileUrl).pipe(
      map(data => {
        const processedData: object = this.normalizeValuesToStrings(data) as object;
        LocalStorageUtils.setKeyValue(`${assetType}-${lang}`, JSON.stringify(processedData));
        return processedData;
      }),
    );
  }

  /**
   * Determines the store identifier for configurations and translations.
   * - For `translations`, constructs a language-specific store identifier.
   * - Handles scenarios where the environment is `local` or Arakh is not operational.
   * - Returns a default or environment-specific store identifier based on the input parameters.
   *
   * @param {boolean} isArakhWorking - Indicates whether Arakh is operational.
   * @param {AssetTypes} typeOfCall - The type of data being fetched (`translations`, `feature-toggles`, or `configs`).
   * @param {string} lang - The language identifier.
   * @param {string} store - The store identifier.
   *
   * @returns {string} - The formatted store identifier.
   * @private
   */
  private _getStoreConfiguration(isArakhWorking: boolean, typeOfCall: AssetTypes, lang: string, store: string): string {
    if (typeOfCall === AssetTypes.TRANSLATIONS) {
      return this._getStoreIdForTranslations(isArakhWorking, lang, store);
    }
    return this._getStoreIdForTogglesAndConfigs(isArakhWorking, store);
  }

  /**
   * Determines the store identifier for translation assets.
   * - Constructs a language-specific store identifier based on the input parameters.
   * - Handles scenarios where the environment is `local` or Arakh is not operational.
   * - Returns a default or environment-specific store identifier.
   *
   * @param {boolean} isArakhWorking - Indicates whether Arakh is operational.
   * @param {string} lang - The language identifier (e.g., 'en', 'fr').
   * @param {string} store - The store identifier.
   *
   * @returns {string} - The formatted store identifier for translations.
   * @private
   */
  private _getStoreIdForTranslations(isArakhWorking: boolean, lang: string, store: string): string {
    const [language]: string[] = lang.split('-');
    const languageCode: string = store ? `-${language}_${store}` : '-default';

    if (this.env === 'local' || !isArakhWorking) {
      // call on arakh edge failed, so we need to use local files
      if (!isArakhWorking && store && this.env !== 'local') {
        return this._processStoreWhenArakhFails(store);
      }
      return store ? languageCode : '-en';
    }

    // store exist / not exist and Arakh works
    return languageCode + (store && this.env !== 'production' && this.env !== 'defaults' ? '-' + this.env : '');
  }

  /**
   * Processes the store identifier when Arakh is not operational and the environment is not local.
   * - Extracts the store value from the identifier.
   * - Replaces 'default' with 'en' if applicable.
   * - Returns the formatted store identifier.
   *
   * @param {string} store - The store identifier.
   * @returns {string} - The processed store identifier.
   * @private
   */
  private _processStoreWhenArakhFails(store: string): string {
    store = store.split('-')[1];
    if (store === 'default') {
      store = 'en';
    }
    return store ? `-${store}` : '';
  }

  /**
   * Determines the store identifier for non-translation assets (e.g., feature toggles or configurations).
   * - Handles scenarios where the environment is `local` or Arakh is not operational.
   * - Returns a default or environment-specific store identifier based on the input parameters.
   *
   * @param {boolean} isArakhWorking - Indicates whether Arakh is operational.
   * @param {string} store - The store identifier.
   * @returns {string} - The formatted store identifier.
   * @private
   */
  private _getStoreIdForTogglesAndConfigs(isArakhWorking: boolean, store: string): string {
    if (this.env === 'local' || !isArakhWorking) {
      // call on arakh edge failed, so we need to use local files
      if (!isArakhWorking && store && this.env !== 'local') {
        store = store.split('-')[1];
        if (store === 'default') {
          return '';
        }
      }
      return store ? `-${store}` : '';
    }
    // store exist / not exist and Arakh works
    return store ? `-${store}-${this.env}` : '-default';
  }

  /**
   * Merges two sets of translations (default and localized) into a single object.
   * - Performs HTTP GET requests to fetch the default and localized translations.
   * - Combines the translations into one object and stores it in local storage.
   * - Normalizes the merged data so all values are strings.
   *
   * @param {string} lang - The language identifier.
   * @param {string} localDefaultFileUrl - A string representing the URL of the default file for local assets.
   * @param {string} localLocalizedFileUrl - A string representing the URL of the localized file for local assets.
   * @param {string} assetType - A string representing the type of asset being processed (e.g., lang-data, feature-toggles-data, etc.).
   *
   * @returns {Observable<object>} - An observable containing the merged translations.
   * @private
   */
  private _mergeLocalAssets(lang: string, localDefaultFileUrl: string, localLocalizedFileUrl: string, assetType: string): Observable<object> {
    return forkJoin<[object, object]>([
      this.httpClient.get<object>(localDefaultFileUrl),
      this.httpClient.get<object>(localLocalizedFileUrl),
    ]).pipe(
      map(([defaultTranslations, localizedTranslations]: [object, object]) => {
        const mergedData: object = {
          ...defaultTranslations,
          ...localizedTranslations,
        };
        const processedData: object = this.normalizeValuesToStrings(mergedData) as object;
        LocalStorageUtils.setKeyValue(`${assetType}-${lang}`, JSON.stringify(processedData));

        return processedData;
      }),
    );
  }

  /**
   * Clears unused language data from local storage.
   * - Retrieves all available languages across all stores.
   * - Identifies languages not used by the current store.
   * - Removes cached data for unused languages, including update dates, translations, feature toggles, and configs.
   *
   * @private
   */
  private _clearUnusedCachedTranslationData(): void {
    const allLanguages: string[] = environment.stores.flatMap((store: any) => store.languages.map((language: any) => language.id));
    const allLanguagesForCurrentStore: Set<string> = new Set(AppUtils.getCurrentStore().languages.map((language: any) => language.id));
    const unusedLanguages: string[] = allLanguages.filter((language: string) => {
      return !allLanguagesForCurrentStore.has(language);
    });

    unusedLanguages.forEach((unusedLang: string) => {
      LocalStorageUtils.clearKey(`lang-update-date-${unusedLang}`);
      LocalStorageUtils.clearKey(`lang-data-${unusedLang}`);
      LocalStorageUtils.clearKey(`feature-toggles-data-${unusedLang}`);
      LocalStorageUtils.clearKey(`config-data-${unusedLang}`);
    });
  }

  /**
   * Sets the next update date for the given language in local storage.
   * The update date is set to the next full hour from the current time.
   *
   * @param {string} lang - The language identifier.
   * @private
   */
  private _setUpdateDate(lang: string): void {
    const nextUpdateDate: Date = new Date();
    nextUpdateDate.setHours(nextUpdateDate.getHours() + 1, 0, 0, 0);

    LocalStorageUtils.setKeyValue(`lang-update-date-${lang}`, nextUpdateDate.getTime().toString());
  }

  /**
   * Determines if an update is required for the given language data.
   * Checks if any of the required data (`lang-data`, `config-data`, or `feature-toggles-data`)
   * is missing in local storage or if the update date has passed.
   *
   * @param {string} lang - The language identifier.
   * @returns {boolean} - Returns `true` if an update is required, otherwise `false`.
   * @private
   */
  private _isUpdateRequired(lang: string): boolean {
    const updateDate: number = Number(LocalStorageUtils.getKeyValue(`lang-update-date-${lang}`));

    return ((Date.now() - updateDate) > 0) || (
      !LocalStorageUtils.getKeyValue(`lang-data-${lang}`) ||
      !LocalStorageUtils.getKeyValue(`config-data-${lang}`) ||
      !LocalStorageUtils.getKeyValue(`feature-toggles-data-${lang}`));
  }
}
